<template>
  <div
    class="aha-presentation-editor-wrapper"
  >
    <SocketError />
    <LoadingScreen v-if="!isRendering" />

    <div class="flex flex-1 min-h-0">
      <div
        class="relative flex min-h-0"
      >
        <MiddleForSharingView
          class="aha-middle"
          @nextSlideHandler="nextSlideHandler"
        />
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'

import { mapActions, mapGetters, mapState } from 'vuex'
import * as Sentry from '@sentry/vue'
import { SET_CURRENT_SLIDE_MODE, WINDOWED_MODE } from '@/vuex/types'
import PresentationApi from '@/api/presentation'

import LoadingScreen from '@/components-v2/common/loading/LoadingScreen.vue'
import MiddleForSharingView from '@/components/presentations/MiddleForSharingView.vue'
import SocketError from '@/components/UI/SocketError'
import PresentationEditorSocketHandlerForSharingView from '@/utils/presentationEditorSocketHandlerForSharingView.js'
import Config from '@/config'
import Utils from '@/util'
import PresentationDetailUtils from '@/utils/PresentationDetailUtils'
import GetDataPresentationDetailUtils from '@/utils/GetDataPresentationDetailUtils'
import RenderCanvasBlocksUtils from '@/utils/renderCanvasBlocksUtils'

import Socket from '@/socket'
import Pricing from '@/pricing'

import EventBus from '@/eventBus'

import cookie from '@/services/cookie'
import { handleLoadAllImageCache } from '@/utils/indexedDB/image.cache.utils'

import constants from '@/constant'
import { detectSafari } from '@/utils/dom.utils'

export default {
  components: {
    MiddleForSharingView,
    LoadingScreen,
    SocketError,
  },
  data() {
    return {
      isRendering: false,
      onlineCount: 0,
      sockets: {},
    }
  },
  computed: {
    ...mapState({ // Do not delete anything here, since they might be called via imported functions
      presentation: (state) => state.presentation,
      presentations: (state) => state.presentations,
      sharePresentation: (state) => state.sharePresentation,
      isNewPresentation: (state) => state.isNewPresentation,
      slideActive: (state) => state.slideActive,
      socketId: (state) => state.socketId,
      isGuestUser: (state) => state.currentUser.isGuest,
      currentUser: (state) => state.currentUser,
      userOnline: (state) => state.userOnline,
      onlineStatus: (state) => state.onlineStatus,
      plan: (state) => state.plan,
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
      isAdmin: (state) => state.isAdmin,
    }),
    ...mapGetters(['isPresenting']),
    ...mapGetters('blankPresentation', ['canUseOnBoardingBlankPresentation']),

  },

  created() {
    // prevent editor from scrolling
    document.body.classList.add('overflow-hidden')
    document.body.classList.add('fixed') // https://ahaslides.atlassian.net/browse/AHA-2696 -> fix simiar to using legacy's .no-scroll
  },
  destroyed() {
    document.body.classList.remove('overflow-hidden')
    document.body.classList.remove('fixed') // https://ahaslides.atlassian.net/browse/AHA-2696 -> fix simiar to using legacy's .no-scroll
    document.body.classList.remove('presentation-detail-layout')

    PresentationEditorSocketHandlerForSharingView.disconnectSocket(this)
    EventBus.$off('onKeyboard')
    EventBus.$off('getDataPresentationDetail')
    this.$store.state.audiences = {}
    this.$store.state.userOnline = 0
    this.$store.state.questions = []
    this.$store.state.tabQnaQuestion = 1
    this.$store.state.lastQuestion = {}
    this.$store.state.questionCount = 0
    this.$store.state.showQnaSession = false
    this.$store.state.presentation.id = null
    this.$store.state.presentationEditors = []
    this.$store.state.openImportModal = false
    this.$store.commit('unsetPresentation')

    window.removeEventListener('message')
  },
  async mounted() {
    window.addEventListener('message', this.handleMessageTemplatePreview, false)
    const { capture: isScreenCapture } = this.$route.query
    const { thumbnail: isCaptureContentSlideThumbnail } = this.$route.query

    this.$store.state.sharePresentation = true
    if (isScreenCapture) {
      this.$store.state.isScreenCapture = true
      this.importFontsForCapture()
    }
    if (isCaptureContentSlideThumbnail) {
      this.$store.state.isCaptureContentSlideThumbnail = true
    }
    this.$store.state.isNewPresentation = Utils.getParamValue('new', window.location.href) === 'true'

    document.body.classList.add('presentation-detail-layout')
    if (!this.$store.state.currentUser.id && cookie.getValue('ahaToken')) {
      await this.getCurrentUserAction()
    } else if (this.$store.state.currentUser.id) {
      this.$store.state.plan = Pricing.getPlanByType(
        this.$store.state.currentUser.plan,
      )
    }

    document.onkeydown = (e) => {
      EventBus.$emit('onKeyboard', e)
    }

    this.$store.state.isTouchScreen = Utils.checkTouchScreen()

    detectSafari()

    PresentationEditorSocketHandlerForSharingView.handleSocketEvent(this)
    EventBus.$on('getDataPresentationDetail', () => {
      this.getDataPresentationDetail()
    })

    // Now load
    await this.getDataPresentationDetail()
    setTimeout(() => {
      this.$store.dispatch(SET_CURRENT_SLIDE_MODE, WINDOWED_MODE)
      window.dispatchEvent(new Event('resize'))
    }, 500)
    this.initSocket(this.presentation.uniqueAccessCode)

    // Support realtime in "share" view
    // Using 'join-user' event for convenient :(
    Socket.getSocket().emit('join-user', `${this.$store.state.presentation.uniqueAccessCode}-presenter`)
  },
  methods: {
    ...mapActions([
      'getCurrentUserAction',
      'patchPresentationAction',
      'setErrorOnInternalServerAction',
      'updateSlideActiveAction',
      'patchSlideByIdAction',
      'checkSlideMovingDirectionAction',
    ]),
    ...mapActions('presentationAttributeModule', ['getPresentationAttributeAction']),
    ...mapActions('featureToggles', ['handlePresentationLoadFlagUnleashByShareCode']),

    updatePresentationSessionOnlineCount: _.throttle((presentationId, onlineCount) => {
      PresentationApi.updateOnlineCountByPresentationId(presentationId, { onlineCount })
    }, 5000),

    handleMessageTemplatePreview(msg) {
      if (msg.data.type === constants.Events.EVENT_CHANGE_SLIDE) {
        const { presenterDomain } = Config
        let whiteLabelDomain = null
        if ((this.presentation && this.presentation.whiteLabelLink)
        || this.$store.state.presentation.whiteLabelLink) {
          if (this.presentation && this.presentation.whiteLabelLink) {
            whiteLabelDomain = this.presentation.whiteLabelLink
          } else {
            whiteLabelDomain = this.$store.state.presentation.whiteLabelLink
          }
        }
        if (whiteLabelDomain && `https://${whiteLabelDomain}` !== msg.origin) {
          return
        }
        if (
          presenterDomain !== msg.origin
          && Config.ssrDomain !== msg.origin
          && Config.audienceDomain !== msg.origin
        ) {
          return
        }
        const { id } = msg.data.slide

        EventBus.$emit('template-preview:go-to-slide', id)
      }
    },

    async getDataPresentationDetail() {
      let { id: presentationId } = this.$route.params
      if (Utils.getShareCodeFromSlug(presentationId) !== null) {
        presentationId = Utils.getShareCodeFromSlug(presentationId)
      }

      await this.handlePresentationLoadFlagUnleashByShareCode(presentationId)
      const payloadDataPresentation = {
        socketId: Socket.getSocketId(),
      }

      try {
        const res = await PresentationApi.getPresentationDetailsByShareCode(presentationId, payloadDataPresentation)
        await this.getPresentationAttributeAction(res?.body?.id)
        await this.handleDataPresentationDetail(res)

        const { preview: isPreviewPresentation } = this.$route.query

        if (isPreviewPresentation) {
          this.$store.state.isPreview = true
        }

        EventBus.$emit('updateLastSlide')
      } catch (err) {
        Sentry.captureException(err)
        GetDataPresentationDetailUtils
          .handleGetDataPresentationDetailFailed(err, this, presentationId)
      }
    },

    async handleDataPresentationDetail(res) {
      const _dispatch = this.$store.dispatch

      this.sockets[Socket.getSocketId()] =
        PresentationDetailUtils.setPresentationSockets(this.currentUser)

      res.body.slides = res.body.Slides

      this.patchPresentationAction({
        submitSlideLookup: res.body.submitSlideLookup || null,
      })

      await handleLoadAllImageCache(res.body.slides)

      this.$store.state.userOnline = res.body.userOnline

      this.$store.state.onlineStatus = 1

      PresentationDetailUtils.setPresentationLanguage(res, _dispatch)

      PresentationDetailUtils.setBrowserTitle(res, this.isPresenterWhiteLabel)

      if (this.canUseOnBoardingBlankPresentation) {
        PresentationDetailUtils.sortAndUpdateSlidesV2(res, this)
      } else {
        PresentationDetailUtils.sortAndUpdateSlides(res, this)
      }

      this.$store.dispatch('setPresentation', res.body)

      PresentationDetailUtils.handleActiveTab(this, _dispatch, res)
      EventBus.$emit('setPresentationDataDone')

      if (!res.body.activeSlide && res.body.slides.length) {
        if (!res.body.slides[0].type) {
          this.$store.state.isNewPresentation = true
        }
      }

      PresentationDetailUtils.setPrezFontFamily(this.presentation.fontFamily)

      PresentationDetailUtils.getAudienceOnline(res, this.slideActive, this.presentation)

      this.handleCacheImage(res.body)
      this.getQuestions(res.body.id, res.body.slides)
      await RenderCanvasBlocksUtils.loadContentSlidesCanvasBlocks(this, res)
      setTimeout(async () => {
        this.isRendering = true
        // EventBus.$emit('eventHandleSetValueTransition')
      }, 500)
      PresentationDetailUtils.detectScreenAndSetTransition()
    },

    initSocket(channel) {
      PresentationEditorSocketHandlerForSharingView.initSocket(channel, this)
    },

    nextSlideHandler(currentIndex) {
      const nextSlide = this.$store.state.presentation.slides[currentIndex + 1]
      if (nextSlide && this.$store.state.sharePresentation) {
        const arr = []
        this.buildImagesList(nextSlide, arr)
        this.preloadImages(arr)
      }
    },

    handleCacheImage(doc) {
      const arr = []
      if (doc && doc.slides) {
        if (doc.logo) {
          arr.push(doc.logo.thumbnail)
          arr.push(doc.logo.large)
        }

        // If is on Sharing view, preload only 3 slides
        doc.slides.slice(0, 3).forEach((slide) => this.buildImagesList(slide, arr))
      }

      this.preloadImages(arr)
    },

    buildImagesList(slide, arr) {
      if (slide.image) {
        arr.push(slide.image.thumbnail)
        arr.push(slide.image.xLarge)
      }

      if (slide.backgroundImage) {
        arr.push(slide.backgroundImage.thumbnail)
        arr.push(slide.backgroundImage.xLarge)
      }

      slide.SlideOptions.forEach((option) => {
        if (option.image) {
          arr.push(option.imageCropped)
        }
      })
    },

    preloadImages(array) {
      if (!this.preloadImages.list) {
        this.preloadImages.list = []
      }
      const { list } = this.preloadImages
      for (let i = 0; i < array.length; i++) {
        const img = new Image()
        img.onload = (() => {
          const index = list.indexOf(this)
          if (index !== -1) {
            // remove image from the array once it's loaded
            // for memory consumption reasons
            list.splice(index, 1)
          }
        })
        list.push(img)
        img.src = array[i]
      }
    },

    getQuestions(id) {
      this.$http
        .get(Config.api.getQuestion, {
          params: {
            id,
          },
        })
        .then(
          (res) => {
            Utils.updateDataQuestionAnswer(
              res.body,
              this.$store.state.tabQnaQuestion,
            )
          },
          () => {},
        ).catch((err) => {
          this.$router.push('/apps/presentations')
          this.setErrorOnInternalServerAction('Failed to get questions list')
          throw err
        })
    },

    importFontsForCapture() {
      // dynamic imports, no worries about bundle size
      import('@fontsource/merriweather')
      import('@fontsource/roboto')
      import('@fontsource/source-code-pro')
      import('@fontsource/montserrat')
      import('@fontsource/oswald')
      import('@fontsource/eb-garamond')
      import('@fontsource/cairo')
      import('@fontsource/roboto-slab')
      import('@fontsource/kanit')
      import('@fontsource/open-sans')
    },

  },
}
</script>
